import React, { useState, useContext, useEffect } from 'react';
import { Box, Typography, TextField, Button, CircularProgress, Alert, Stepper, Step, StepLabel, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import StepIconOutlined from '../StepIconOutlined';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';
// import EnterPracticeNpiStep from './EnterPracticeNpiStep';
import ConfirmPracticeDetailsStep from './ConfirmPracticeDetailsStep';
import InHouseCollectionStep from './InHouseCollectionStep';

/**
 * Wizard for PendingOfficeStaff: 2 steps
 * 1) Enter Practice NPI
 * 2) Confirm Practice Details and submit
 */
const SetupOfficeStaffWizard = ({ practiceRequest, onUpdate }) => {
  const { user, tokens } = useContext(AuthContext);
  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  // Step 1: enter practice NPI; Step 2: confirm details
  const [step, setStep] = useState(practiceRequest?.practice_npi ? 2 : 1);
  const steps = ['Enter NPI', 'Collections on-site', 'Confirm details'];
  const [practiceNpi, setPracticeNpi] = useState(practiceRequest?.practice_npi || '');
  const [practiceNpiData, setPracticeNpiData] = useState(null);
  const [details, setDetails] = useState({
    practice_npi: practiceRequest?.practice_npi || '',
    practice_name: practiceRequest?.practice_name || '',
    corporate_street_1: practiceRequest?.corporate_street_1 || '',
    corporate_street_2: practiceRequest?.corporate_street_2 || '',
    corporate_city: practiceRequest?.corporate_city || '',
    corporate_state: practiceRequest?.corporate_state || '',
    corporate_zip_code: practiceRequest?.corporate_zip_code || '',
    billing_street_1: practiceRequest?.billing_street_1 || '',
    billing_street_2: practiceRequest?.billing_street_2 || '',
    billing_city: practiceRequest?.billing_city || '',
    billing_state: practiceRequest?.billing_state || '',
    billing_zip_code: practiceRequest?.billing_zip_code || '',
    shipping_street_1: practiceRequest?.shipping_street_1 || '',
    shipping_street_2: practiceRequest?.shipping_street_2 || '',
    shipping_city: practiceRequest?.shipping_city || '',
    shipping_state: practiceRequest?.shipping_state || '',
    shipping_zip_code: practiceRequest?.shipping_zip_code || '',
    practice_phone: practiceRequest?.practice_phone || '',
    practice_fax: practiceRequest?.practice_fax || '',
    practice_email: practiceRequest?.practice_email || '',
    practice_website: practiceRequest?.practice_website || '',
    // Additional info used by ConfirmPracticeDetailsStep
    practice_dba: practiceRequest?.practice_dba || '',
    practice_ein: practiceRequest?.practice_ein || '',
    practice_state_tax_id: practiceRequest?.practice_state_tax_id || '',
    practice_request_tax_exemption: practiceRequest?.practice_request_tax_exemption || false,
    practice_type: practiceRequest?.practice_type || ''
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [doingInHouse, setDoingInHouse] = useState(Boolean(practiceRequest?.practice_is_doing_in_house_collection));
  const [inHouseAgreed, setInHouseAgreed] = useState(practiceRequest?.agreement_key_for_in_house_collection === 'ifu');

  // Load existing NPI data if present
  useEffect(() => {
    const fetchNpi = async (npi) => {
      setLoading(true);
      try {
        const res = await fetch(`${apiBase}/npi?number=${npi}`, {
          headers: { Authorization: `Bearer ${tokens.id_token}` }
        });
        const data = await res.json();
        if (!res.ok || data.Errors || data.result_count === 0) {
          throw new Error(data.Errors?.[0]?.description || 'No NPI found');
        }
      const rec = data.results[0];
      setPracticeNpiData(rec);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    if (practiceRequest?.practice_npi) {
      fetchNpi(practiceRequest.practice_npi);
    }
  }, [practiceRequest?.practice_npi]);

  const handleValidate = async () => {
    setError(null);
    if (!practiceNpi) {
      setError('Practice NPI is required');
      return;
    }
    // Fetch NPI data
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/npi?number=${practiceNpi}`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` }
      });
      const data = await res.json();
      if (!res.ok || data.Errors || data.result_count === 0) {
        throw new Error(data.Errors?.[0]?.description || 'No NPI found');
      }
      const rec = data.results[0];
      setPracticeNpiData(rec);
      // Suggest preferred org NPI if personal
      if (rec.enumeration_type === 'NPI-1') {
        setError('Note: A practice/organization NPI is preferred if available.');
      }
      // Auto-populate practice details like provider wizard
      const addresses = rec.addresses || [];
      let mailing = addresses.find(a => a.address_purpose?.toUpperCase() === 'MAILING');
      if (!mailing && addresses.length > 0) mailing = addresses[0];
      setDetails(d => ({
        ...d,
        practice_npi: practiceNpi,
        practice_name: rec.basic?.organization_name || d.practice_name,
        corporate_street_1: mailing?.address_1 || d.corporate_street_1,
        corporate_street_2: mailing?.address_2 || d.corporate_street_2,
        corporate_city: mailing?.city || d.corporate_city,
        corporate_state: mailing?.state || d.corporate_state,
        corporate_zip_code: mailing?.postal_code || d.corporate_zip_code,
        practice_phone: mailing?.telephone_number || d.practice_phone,
        practice_fax: mailing?.fax_number || d.practice_fax,
        // Default to user's email if NPI record has no email
        practice_email: rec.basic?.email || practiceRequest.user_email || d.practice_email,
        practice_type: d.practice_type || (rec.taxonomies?.[0]?.desc || d.practice_type),
      }));
      setStep(2);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDetailChange = (field) => (e) => {
    const { type, value, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;
    setDetails((d) => ({ ...d, [field]: val }));
  };

  // Validate and move to in-house step
  const handleContinueFromDetails = () => {
    setError(null);
    if (validateDetails()) {
      setStep(3);
    }
  };

  const validateDetails = () => {
    const errs = {};
    if (!details.practice_name) errs.practice_name = 'Practice Name is required';
    // Corporate address
    if (!details.corporate_street_1) errs.corporate_street_1 = 'Corporate Street Address is required';
    if (!details.corporate_city) errs.corporate_city = 'Corporate City is required';
    if (!details.corporate_state) errs.corporate_state = 'Corporate State is required';
    if (!details.corporate_zip_code) errs.corporate_zip_code = 'Corporate ZIP Code is required';
    // Billing address
    if (!details.billing_street_1) errs.billing_street_1 = 'Billing Street Address is required';
    if (!details.billing_city) errs.billing_city = 'Billing City is required';
    if (!details.billing_state) errs.billing_state = 'Billing State is required';
    if (!details.billing_zip_code) errs.billing_zip_code = 'Billing ZIP Code is required';
    // Shipping address
    if (!details.shipping_street_1) errs.shipping_street_1 = 'Shipping Street Address is required';
    if (!details.shipping_city) errs.shipping_city = 'Shipping City is required';
    if (!details.shipping_state) errs.shipping_state = 'Shipping State is required';
    if (!details.shipping_zip_code) errs.shipping_zip_code = 'Shipping ZIP Code is required';
    // Contact and IDs
    if (!details.practice_ein) errs.practice_ein = 'EIN is required';
    if (!details.practice_state_tax_id) errs.practice_state_tax_id = 'State Tax ID is required';
    if (!details.practice_phone) errs.practice_phone = 'Practice Phone Number is required';
    if (!details.practice_fax) errs.practice_fax = 'Fax Number is required';
    if (!details.practice_email) errs.practice_email = 'Email is required';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    setError(null);
    // Enforce required fields on step two
    const ok = validateDetails();
    if (!ok) return;
    setLoading(true);
    try {
      const payload = {
        ...details,
        // In-house collection agreement
        practice_is_doing_in_house_collection: Boolean(doingInHouse && inHouseAgreed),
        agreement_key_for_in_house_collection: (doingInHouse && inHouseAgreed) ? 'ifu' : null,
        status: 'Submitted'
      };
      const res = await fetch(`${apiBase}/users/${user.id}/setup-practice-request`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.id_token}`
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to submit');
      }
      const updated = await res.json();
      onUpdate(updated);
      navigate('/');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // copy helpers
  const handleCopyCorporateToBilling = () => {
    setDetails(d => ({
      ...d,
      billing_street_1: d.corporate_street_1,
      billing_street_2: d.corporate_street_2,
      billing_city: d.corporate_city,
      billing_state: d.corporate_state,
      billing_zip_code: d.corporate_zip_code,
    }));
  };
  const handleCopyBillingToShipping = () => {
    setDetails(d => ({
      ...d,
      shipping_street_1: d.billing_street_1,
      shipping_street_2: d.billing_street_2,
      shipping_city: d.billing_city,
      shipping_state: d.billing_state,
      shipping_zip_code: d.billing_zip_code,
    }));
  };
  const handleCopyCorporateToShipping = () => {
    setDetails(d => ({
      ...d,
      shipping_street_1: d.corporate_street_1,
      shipping_street_2: d.corporate_street_2,
      shipping_city: d.corporate_city,
      shipping_state: d.corporate_state,
      shipping_zip_code: d.corporate_zip_code,
    }));
  };
  return (
    <Box sx={{ maxWidth: 600 }}>
      {/* Progress indicator */}
      <Stepper activeStep={step - 1} sx={{ mb: 3 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel StepIconComponent={StepIconOutlined}>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      {step === 1 ? (
        <>
          <Typography variant="h6" gutterBottom>
            Enter practice NPI
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            We accept both personal and organization NPIs. An organization NPI is preferred if available.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Practice NPI Number"
              value={practiceNpi}
              onChange={(e) => setPracticeNpi(e.target.value)}
              error={!!error && !error.startsWith('Note:')}
              helperText={error && !error.startsWith('Note:') ? error : ''}
            />
            {error && error.startsWith('Note:') && (
              <Alert severity="info" sx={{ mt: 2 }}>{error.replace(/^Note:\s*/, '')}</Alert>
            )}
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button variant="outlined" onClick={() => navigate('/')}>Back</Button>
              <Button
                variant="contained"
                onClick={handleValidate}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Validate NPI'}
              </Button>
            </Box>
          </Box>
        </>
      ) : step === 2 ? (
        <InHouseCollectionStep
          value={doingInHouse}
          agreed={inHouseAgreed}
          onChange={setDoingInHouse}
          onAgreeChange={setInHouseAgreed}
          onBack={() => setStep(1)}
          onContinue={() => setStep(3)}
          continueLabel="Continue"
        />
      ) : (
        <ConfirmPracticeDetailsStep
          practiceNpiData={practiceNpiData}
          details={details}
          errors={fieldErrors}
          onDetailChange={handleDetailChange}
          onCopyBilling={handleCopyBillingToShipping}
          onCopyCorporateToBilling={handleCopyCorporateToBilling}
          onCopyCorporateToShipping={handleCopyCorporateToShipping}
          onBack={() => setStep(2)}
          onContinue={handleSubmit}
          continueLabel="Submit"
        />
      )}
      {/* Confirmation dialog not used in new flow; kept for potential future use */}
    </Box>
  );
};

export default SetupOfficeStaffWizard;
